import Immutable from 'seamless-immutable';
import { TextField } from '@material-ui/core';
import { isWidthDown } from '@material-ui/core/withWidth';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import CraverRadio from '../components/share/CraverRadio';
import Dropdown from '../components/share/Dropdown';
import Hours from '../components/share/Hours';
import DisplayImages from '../components/share/DisplayImages';
import * as Constants from '../data/Constants';
import * as English from '../locales/EN/translation.json';
import * as localeSetting from '../config/locale.json';
import * as Authorization from './AuthorizationChecks';
/**
 *
 * @param items an array of strings
 */
export const listArrayToString = (items) => {
  if (!items) {
    return '';
  }
  const mutableItems = Immutable.asMutable(items, { deep: true });

  if (items.length > 1) {
    const lastIndex = items.length - 1;
    const lastItem = items[lastIndex];
    mutableItems[lastIndex] = `and ${lastItem}`;
  }

  if (mutableItems.length === 2) {
    return mutableItems.join(' ');
  }

  return mutableItems.join(', ');
};

const monthDict = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export const getMonthName = month => ((month > 0 && month < 13) ? monthDict[month] : null);


/**
 *  If fieldData is null, undefined, NaN, empty string (""), 0, or false => show as N/A
fieldData for Birth Day and Birth Month have default vals of 0 => show as N/A
 * */
export const formatFieldData = (field, fieldData) => {
  if (field.type === 'boolean') return fieldData ? 'true' : 'false';
  if (!fieldData) return 'N/A';
  if (['category', 'productTax', 'categoryTax', 'tier'].includes(field.key)) return fieldData.name;
  if (field.key == 'birthMonth') return getMonthName(fieldData);
  return fieldData;
};

export const showAvailableOptions = (customizations) => {
  const availOptions = [];
  if (customizations) {
    customizations.map((customization) => {
      const placeHolder = {
        id: customization.id,
        name: customization.name,
        isDisabled: true,
      };
      availOptions.push(placeHolder);
      availOptions.push.apply(availOptions, customization.options);
    });
  }
  return availOptions;
};

export const getFieldTypeTag = (fieldType) => {
  switch (fieldType) {
    case 'image':
      return DisplayImages;
    case 'hours':
      return Hours;
    case 'dropdown':
      return Dropdown;
    case 'boolean':
      return CraverRadio;
    default:
      return TextField;
  }
};

/**
 * the usage is similar to mapStateToProps
 * returns null if no resourceToMatch is 0, null, undefined or an empty object
 * @param {*} resources
 * @param {*} match
 * @param {*} newResources
 */
export const findEntityByID = ({
  id,
  match,
  newResources,
  resources,
}) => {
  const resourceToMatch = newResources || resources;
  if (!resourceToMatch || Object.keys(resourceToMatch).length == 0) return null;
  if (Object.keys(resourceToMatch).length != 0) {
    const { resource, resourceId } = match.params;
    const finalId = id || resourceId;
    const entities = resourceToMatch[resource];
    const data = entities.filter(item => item.id.toString() === finalId.toString());
    return data[0];
  }
};

/**
 * the usage is similar to mapStateToProps
 * returns null if findEntityByID returns null
 * @param {*} resources
 * @param {*} match
 * @param {*} newResources
 */
export const findSubEntityByID = (findEntityArgs) => {
  const { match } = findEntityArgs;
  const { subResource, subResourceId } = match.params;
  const entity = findEntityByID(findEntityArgs);
  if (entity) {
    const subEntities = entity[subResource];
    if (subEntities) {
      const data = subEntities.filter(item => item.id.toString() === subResourceId.toString());
      return data[0];
    }
  } else {
    return null;
  }
};

export const depluralizeResourceName = (resourceName) => {
  switch (resourceName.toLowerCase()) {
    case 'categories':
      return 'category';
    case 'customizations':
      return 'modifier group';
    case 'taxes':
      return 'tax';
    case 'companies':
      return 'company';
    case 'zones':
      return 'delivery zone';
    default:
      return resourceName.substring(0, resourceName.length - 1);
  }
};

export const changeResourceDisplayName = (resourceName, resourceOptions) => {
  let resourceTitle = resourceName;
  resourceTitle = resourceOptions[resourceName] ? resourceOptions[resourceName] : resourceName;
  return resourceTitle;
};

/**
 * @param milliseconds a number
 * @returns date string with form yyyymmdd
 */
export const formMillisecondToDateString = (milliseconds) => {
  const timePoint = Date.now() - milliseconds * 1000;
  const time = new Date(timePoint);
  const year = time.getFullYear();
  const month = (time.getMonth() + 1).toLocaleString('en-us', { minimumIntegerDigits: 2, useGrouping: false });
  const day = time.getDate().toLocaleString('en-us', { minimumIntegerDigits: 2, useGrouping: false });
  const dateString = `${year}${month}${day}`;
  return dateString;
};

export const getSelectedOptions = (event, dropdownOptions, allowMultiple, fieldKey) => {
  const selectedId = event.target && event.target.value;
  let selectedOptions = dropdownOptions && dropdownOptions.filter(
    option => (allowMultiple ? selectedId.includes(option.id) : option.id === selectedId),
  );

  const isSelectAllSelected = Array.isArray(selectedId) && selectedId.includes('all');
  if (fieldKey && Constants.fieldsWithSelectAllOption[fieldKey] && isSelectAllSelected) {
    // Unselects everything only if you start with all options selected.
    if (Array.isArray(dropdownOptions) && selectedOptions.length === dropdownOptions.length) {
      return [];
    }
    return dropdownOptions;
  }
  return selectedOptions = allowMultiple ? selectedOptions : selectedOptions && selectedOptions[0];
};

export const craverStaffAddress = /@admin\.com$/;

export const checkIsLocationAdmin = (adminState) => {
  if (!adminState) return false;
  const { email } = adminState;
  if (!email) return false;

  return !!Authorization.getLocationIdOfAuthorizationOrAdmin('locations', adminState);
};

export const titleCase = string => startCase(lowerCase(string));

export const updateHoursToNewDatabaseFormat = (hours) => {
  const updatedHours = hours.map((day) => {
    const dayCopy = cloneDeep(day);
    if (day.pickupsClosed && day.open && day.close) {
      dayCopy.pickupsClosed = false;
    }
    if (day.deliveriesClosed && day.deliveryOpen && day.deliveryClose) {
      dayCopy.deliveriesClosed = false;
    }
    return dayCopy;
  });
  return updatedHours;
};

export const getProductNameWithCategory = (product) => {
  if (!product || !product.name || !product.category || !product.category.name) return '';
  return `${product.name} • (${product.category.name})`;
};

export const getItemNameWithCategoryAndProduct = (item, product) => {
  if (!item || !item.id || !item.name || !product || !product.name || !product.category || !product.category.name) return '';
  return `${item.name} • (${product.category.name} - ${product.name})`;
};

export const showTwoDecimalsForMoney = (dollarAmount) => {
  if (!dollarAmount && dollarAmount !== 0) return;
  const parsed = parseFloat(dollarAmount);
  if (Number.isNaN(parsed) || parsed === Infinity) return 'N/A';
  return parsed.toFixed(2);
};

export const validatePrice = price => (!price && price !== 0) || !Number.isFinite(Number(price)) || price < 0 || price > 999.99 || /([.][0-9]{3,})/.test(price);

/**
 * Returns a boolean[] with each boolean reporting if the variation/product item is invalid at all.
 * A summary status without
 * reporting which field in particular is invalid.
*/
export const areVariationsInvalid = (newProductItems) => {
  if (!Array.isArray(newProductItems)
      || (Array.isArray(newProductItems) && newProductItems.length === 0)) {
    return [true];
  }
  return newProductItems.map((item) => {
    const {
      attribute_name,
      attribute_value,
      isDefault,
      name,
      price,
    } = item;
    if (!attribute_name || !attribute_value || !name || (!isDefault && isDefault !== false)) {
      return true;
    }
    return validatePrice(price);
  });
};

/**
 * Returns {
 *  isFieldInvalid: {
 *    [itemField: string]: boolean;
 *  },
 *  invalidFieldMessage: {
 *    [itemField: string]: string
 *  },
 * }
 * @param {*} item
 */
export const isItemInvalid = (item) => {
  if (!item) return;
  const requiredFields = ['attribute_name', 'attribute_value', 'isDefault', 'name', 'price'];
  const isFieldInvalid = {};
  const invalidFieldMessage = {};
  requiredFields.forEach((field) => {
    const value = item[field];
    let isInvalid;
    switch (field) {
      case 'attribute_name':
      case 'attribute_value':
      case 'name':
        isInvalid = !value;
        isFieldInvalid[field] = isInvalid;
        invalidFieldMessage[field] = isInvalid ? Constants.validationErrorMessages.items[field] : '';
        break;
      case 'isDefault':
        isInvalid = !value && value !== false;
        isFieldInvalid[field] = isInvalid;
        invalidFieldMessage[field] = isInvalid ? Constants.validationErrorMessages.items.isDefault : '';
        break;
      case 'price':
        isInvalid = validatePrice(value);
        isFieldInvalid[field] = isInvalid;
        invalidFieldMessage[field] = isInvalid ? Constants.validationErrorMessages.items.price : '';
        break;
      default:
        break;
    }
  });
  return { isFieldInvalid, invalidFieldMessage };
};

export const checkAtLeastOneDefaultItem = (items) => {
  let numberOfDefaults = 0;
  items.forEach((item) => {
    if (!item.isDefault || (item.isDefault && item.isDisabled)) return;
    numberOfDefaults += 1;
  });
  return numberOfDefaults;
};

export const buildUpSearchableProductsAndItems = (productsResponse, alsoCreateIdToNameMap) => {
  let searchableProducts = [];
  let searchableProductItems = [];
  const itemNamesForIds = {};
  if (!productsResponse || productsResponse.error || !productsResponse.response) {
    return { searchableProducts, searchableProductItems, itemNamesForIds };
  }
  if (Array.isArray(productsResponse.response)) {
    const buildUpProductsAndItems = (product) => {
      const invalidProduct = !product
        || !product.name
        || !product.category
        || !product.category.name;
      if (!invalidProduct && product.id) {
        const searchableDropdownProduct = {
          label: getProductNameWithCategory(product),
          value: product.id,
        };
        searchableProducts.push(searchableDropdownProduct);
      }
      const { items } = product;
      if (!items || !Array.isArray(items) || (Array.isArray(items) && items.length === 0)) return;
      items.forEach((item) => {
        if (!item || !item.id || !item.name || invalidProduct) return;
        const longItemName = getItemNameWithCategoryAndProduct(item, product);

        if (alsoCreateIdToNameMap) {
          itemNamesForIds[`${item.id}`] = longItemName;
        }
        const searchableDropdownFormatItem = { label: longItemName, value: item.id };
        searchableProductItems.push(searchableDropdownFormatItem);
      });
    };
    productsResponse.response.forEach(buildUpProductsAndItems);

    searchableProducts = sortBy(searchableProducts, ['label', 'value']);
    searchableProductItems = sortBy(searchableProductItems, ['label', 'value']);
    return { searchableProducts, searchableProductItems, itemNamesForIds };
  }
};

export const getItemsAttachedToCustomization = (productsResponse, customizationId) => {
  const attachedItemIds = new Map();
  const allItemCustomizationsAndProductIds = new Map();
  const products = get(productsResponse, 'response');
  if (!Array.isArray(products)) return attachedItems;

  products.forEach((product) => {
    const items = get(product, 'items');
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      const itemId = item.id;
      const customizations = get(item, 'customizations');
      if (!Array.isArray(customizations)) return;

      const itemInfo = new Map();
      itemInfo.set('customizations', customizations);
      itemInfo.set('productId', product.id);
      allItemCustomizationsAndProductIds.set(itemId, itemInfo);

      customizations.forEach((customization) => {
        if (customization.id !== Number(customizationId)) return;
        attachedItemIds.set(itemId, true);
      });
    });
  });

  return { attachedItemIds, allItemCustomizationsAndProductIds };
};

export const convertIdsToSeparatedNames = (resourceClone, idsPath, idToNameMap, separation) => {
  if (!idToNameMap) {
    set(resourceClone, idsPath, '');
    return resourceClone;
  }
  const idStringOrArray = get(resourceClone, idsPath);
  let namesString;
  if (!idStringOrArray) {
    namesString = '';
  }
  if (idStringOrArray) {
    const ids = Array.isArray(idStringOrArray) ? idStringOrArray : idStringOrArray.split(',');
    const names = ids.map(id => idToNameMap[`${id}`]);
    namesString = names.join(separation);
  }
  set(resourceClone, idsPath, namesString);
  return resourceClone;
};

export const makeSubResourceNameList = (
  resource,
  subResourcePath,
  subResourceNamePath,
  makeNewLineSeparated,
) => {
  if (!resource) return '';
  const subResourceArray = get(resource, subResourcePath);
  if (!Array.isArray(subResourceArray)) return '';
  const namesArray = subResourceArray.map((subResource) => {
    const name = get(subResource, subResourceNamePath);
    return typeof name === 'string' ? name : '';
  });
  return namesArray.join(makeNewLineSeparated ? '\n\r' : ', ');
};

export const translation = (path) => {
  const rightTranslationFile = localeSetting.locale === 'EN' && English;
  return get(rightTranslationFile, path, '');
};

export const isAdminEmailAccount = email => /@admin\.com$/.test(email);

export const isDeviceView = (deviceSize, width) => !!width && isWidthDown(deviceSize, width);

export const capitalizeTheFirstLetterOfEachWord = (words) => {
  if (words === 'customizations') return 'Modifiers';
  const separateWord = words.toLowerCase().split(' ');
  for (let i = 0; i < separateWord.length; i++) {
    separateWord[i] = separateWord[i].charAt(0).toUpperCase()
      + separateWord[i].substring(1);
  }
  return separateWord.join(' ');
};

export const sortByPosition = (data) => {
  data.sort((a, b) => a.position - b.position);
};

export const searchEntity = (term, entities, field) => {
  if (!term && term !== 0) return entities;
  const searchedEntities = term && entities && entities.filter((entity) => {
    if (entity[field]) {
      const normalizedSearchTerm = term.toUpperCase();
      const normalizedEntityName = entity[field].toUpperCase();
      if (normalizedEntityName.includes(normalizedSearchTerm)) {
        return normalizedEntityName;
      }
    }
  });
  return searchedEntities;
};

export const getIdsMap = (entityArray, idPath = 'id') => {
  const idMap = {};
  if (!entityArray) return idMap;
  entityArray.forEach((entity) => {
    const id = get(entity, idPath);
    idMap[id] = true;
  });
  return idMap;
};

/**
 * This isn't cloning the entities for performance, so be careful not to mutate values
 * inside them, as this could also mutate values e.g. in the Redux store
 */
export const getIdToEntityMap = (entityArray = [], idPath = 'id') => {
  const map = {};
  if (!entityArray) return map;
  entityArray.forEach((entity) => {
    const id = get(entity, idPath);
    map[id] = entity;
  });
  return map;
};

export const removeKeysFromObjectArray = (entities, keys) => entities.map((entity) => {
  keys.forEach((key) => {
    const value = get(entity, key, null);
    if (value) {
      delete entity[key];
    }
  });
  return entity;
});

export const createRequestData = (detail) => {
  const requestData = {};
  Object.values(detail).forEach(({ key, value }) => {
    requestData[key] = value;
  });
  return requestData;
};
